import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertTriangle, XCircle, Clock, Pencil, Save, X, PlayCircle, RefreshCw } from 'lucide-react';
import { getComplianceControls, updateComplianceControl, runSOC2AutomatedChecks, runHIPAAAutomatedChecks, runGDPRAutomatedChecks } from '../../../services/complianceService';

/**
 * Compliance Dashboard Component
 * Shows SOC 2, HIPAA, and GDPR implementation status
 */
const ComplianceDashboard = () => {
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState('SOC2');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [checkResults, setCheckResults] = useState(null);
  const [runningChecks, setRunningChecks] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  useEffect(() => {
    loadControls();
  }, [selectedFramework]);
  
  const loadControls = async () => {
    setLoading(true);
    const result = await getComplianceControls(selectedFramework);
    if (result?.success) {
      setControls(result?.data || []);
    }
    setLoading(false);
  };

  const startEditing = (control) => {
    setEditingId(control.id);
    setEditForm({
      implementation_status: control.implementation_status,
      implementation_notes: control.implementation_notes || '',
      evidence_location: control.evidence_location || '',
      next_test_date: control.next_test_date || '',
    });
    setSaveError(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
    setSaveError(null);
  };

  const handleRunChecks = async () => {
    setRunningChecks(true);
    setCheckError(null);
    setCheckResults(null);
    const runner = selectedFramework === 'SOC2' ? runSOC2AutomatedChecks
      : selectedFramework === 'HIPAA' ? runHIPAAAutomatedChecks
      : runGDPRAutomatedChecks;
    const result = await runner();
    if (result?.success) {
      setCheckResults(result.results);
      setLastChecked(new Date());
    } else {
      setCheckError(result?.error || 'Automated checks failed.');
    }
    setRunningChecks(false);
  };

  const saveEditing = async (controlId) => {
    setSaving(true);
    setSaveError(null);
    const result = await updateComplianceControl(controlId, {
      ...editForm,
      last_tested_date: editForm.next_test_date ? new Date().toISOString().split('T')[0] : undefined,
    });
    if (result?.success) {
      setControls(prev => prev.map(c => c.id === controlId ? { ...c, ...editForm } : c));
      setEditingId(null);
      setEditForm({});
    } else {
      setSaveError(result?.error || 'Failed to save');
    }
    setSaving(false);
  };
  
  const getStatusColor = (status) => {
    const colors = {
      implemented: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      not_implemented: 'bg-red-100 text-red-800',
      not_applicable: 'bg-gray-100 text-gray-800'
    };
    return colors?.[status] || colors?.not_implemented;
  };
  
  const getStatusIcon = (status) => {
    const icons = {
      implemented: CheckCircle,
      partial: Clock,
      not_implemented: XCircle,
      not_applicable: AlertTriangle
    };
    return icons?.[status] || XCircle;
  };
  
  const getStatusLabel = (status) => {
    const labels = {
      implemented: 'Implemented',
      partial: 'Partial',
      not_implemented: 'Not Implemented',
      not_applicable: 'N/A'
    };
    return labels?.[status] || status;
  };
  
  // Use live check results for stats when available (no DB needed), fall back to DB controls
  const displayStatuses = checkResults
    ? checkResults.map(r => r.status)
    : controls.map(c => c.implementation_status);

  const countStatus = (s) => displayStatuses.filter(x => x === s).length;
  const total = displayStatuses.length;
  const completionRate = total === 0 ? 0 : Math.round((countStatus('implemented') / total) * 100);
  
  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Compliance Status Disclaimer</h3>
            <p className="text-sm text-blue-800">
              <strong>SOC 2:</strong> Controls are SOC 2-aligned but not certified. SOC 2 Type II certification requires independent audit by qualified auditor.<br />
              <strong>HIPAA:</strong> Controls are HIPAA-aligned but not compliant without Business Associate Agreements and independent assessment.<br />
              <strong>GDPR/CCPA:</strong> Privacy tools are GDPR/CCPA-ready. Legal compliance should be verified with legal counsel.
            </p>
          </div>
        </div>
      </div>
      
      {/* Framework Selector + Run Checks */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2">
          {['SOC2', 'HIPAA', 'GDPR']?.map(framework => (
            <button
              key={framework}
              onClick={() => { setSelectedFramework(framework); setCheckResults(null); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedFramework === framework
                  ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              {framework}
            </button>
          ))}
        </div>
        <button
          onClick={handleRunChecks}
          disabled={runningChecks}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors"
        >
          {runningChecks
            ? <><RefreshCw size={16} className="animate-spin" /> Running checks…</>
            : <><PlayCircle size={16} /> Run Automated Checks</>
          }
        </button>
      </div>

      {/* Automated Check Results */}
      {checkError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          <strong>Check failed:</strong> {checkError}
        </div>
      )}
      {checkResults && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <Shield size={18} className="text-primary" />
              <span className="font-semibold text-gray-900">Automated Check Results</span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                checkResults.every(r => r.passed) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {checkResults.filter(r => r.passed).length}/{checkResults.length} passed
              </span>
            </div>
            {lastChecked && (
              <span className="text-xs text-gray-500">
                Last run: {lastChecked.toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {checkResults.map(r => (
              <div key={r.control_id} className="flex items-start gap-3 px-6 py-4">
                <div className="mt-0.5 flex-shrink-0">
                  {r.passed
                    ? <CheckCircle size={18} className="text-green-500" />
                    : <AlertTriangle size={18} className="text-yellow-500" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800 text-sm">{r.control_id}: {r.control_name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.status === 'implemented' ? 'bg-green-100 text-green-800' :
                      r.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {r.status === 'implemented' ? 'Implemented' : r.status === 'partial' ? 'Partial' : 'Not Implemented'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{r.notes}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="px-6 py-3 bg-blue-50 border-t border-blue-100">
            <p className="text-xs text-blue-700">✓ Results have been saved to the controls below. Edit any control to add evidence or override status.</p>
          </div>
        </div>
      )}
      
      {/* Completion Rate */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{selectedFramework} Implementation Status</h3>
          <span className="text-3xl font-bold text-primary">{completionRate}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-primary h-4 rounded-full transition-all duration-500"
            style={{ width: `${completionRate}%` }}
          />
        </div>
        <div className="mt-4 grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-green-600">{countStatus('implemented')}</div>
            <div className="text-sm text-gray-600">Implemented</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-600">{countStatus('partial')}</div>
            <div className="text-sm text-gray-600">Partial</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">{countStatus('not_implemented')}</div>
            <div className="text-sm text-gray-600">Not Implemented</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-600">{countStatus('not_applicable')}</div>
            <div className="text-sm text-gray-600">N/A</div>
          </div>
        </div>
      </div>
      
      {/* Controls List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading controls...</div>
        ) : controls?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No controls found</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {controls?.map(control => {
              const StatusIcon = getStatusIcon(control?.implementation_status);
              const isEditing = editingId === control.id;

              return (
                <div key={control?.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <StatusIcon className={`w-6 h-6 ${
                        control?.implementation_status === 'implemented' ? 'text-green-600' :
                        control?.implementation_status === 'partial' ? 'text-yellow-600' :
                        control?.implementation_status === 'not_implemented'? 'text-red-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {control?.control_id}: {control?.control_name}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">{control?.control_description}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                          {!isEditing && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(control?.implementation_status)}`}>
                              {getStatusLabel(control?.implementation_status)}
                            </span>
                          )}
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => saveEditing(control.id)}
                                disabled={saving}
                                className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                              >
                                <Save size={12} />
                                {saving ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                onClick={cancelEditing}
                                disabled={saving}
                                className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300 disabled:opacity-50"
                              >
                                <X size={12} />
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => startEditing(control)}
                              className="flex items-center gap-1 px-3 py-1 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-100"
                            >
                              <Pencil size={12} />
                              Edit
                            </button>
                          )}
                        </div>
                      </div>

                      {isEditing ? (
                        <div className="mt-3 space-y-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
                          {saveError && (
                            <p className="text-xs text-red-600 font-medium">{saveError}</p>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                            <select
                              value={editForm.implementation_status}
                              onChange={e => setEditForm(f => ({ ...f, implementation_status: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                            >
                              <option value="implemented">Implemented</option>
                              <option value="partial">Partial</option>
                              <option value="not_implemented">Not Implemented</option>
                              <option value="not_applicable">N/A</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Implementation Notes</label>
                            <textarea
                              value={editForm.implementation_notes}
                              onChange={e => setEditForm(f => ({ ...f, implementation_notes: e.target.value }))}
                              rows={3}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                              placeholder="Describe how this control is implemented…"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Evidence Location</label>
                            <input
                              type="text"
                              value={editForm.evidence_location}
                              onChange={e => setEditForm(f => ({ ...f, evidence_location: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                              placeholder="e.g. /docs/soc2/CC6.1 or link to audit evidence"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Next Test Date</label>
                            <input
                              type="date"
                              value={editForm.next_test_date}
                              onChange={e => setEditForm(f => ({ ...f, next_test_date: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          {control?.implementation_notes && (
                            <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-3">
                              <strong>Implementation Notes:</strong> {control?.implementation_notes}
                            </div>
                          )}
                          {control?.evidence_location && (
                            <div className="mt-2 text-sm text-gray-600">
                              <strong>Evidence:</strong> {control?.evidence_location}
                            </div>
                          )}
                          {control?.last_tested_date && (
                            <div className="mt-2 text-sm text-gray-500">
                              Last tested: {new Date(control?.last_tested_date)?.toLocaleDateString()}
                              {control?.next_test_date && (
                                <span className="ml-4">
                                  Next test: {new Date(control?.next_test_date)?.toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceDashboard;